import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import { ButtonBase, Typography, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { observer } from "mobx-react";
import React, { FunctionComponent } from "react";
import { ThemeColors } from "../../Theme/AppTheme";
import { DashboardStore } from "../../stores/Layout/DashboardStore";
import hexToRGB from "../../utils/hexToRGB";
import { useStore } from "utils/useStore";
import { AuthStore } from "stores/AuthStore";
import { HomeRounded, HomeWorkRounded } from "@mui/icons-material";
import { useLocation } from "react-router";
import { trackHomePage } from "components/Signals/SignalsUtils";
import SignalsPageStore from "components/Signals/Store/SignalsPageStore";

const RootDiv = styled("div")(({ theme }) => ({
    height: "48px",
    position: "relative",
    width: "100%",
    paddingLeft: theme.spacing(2.5),
    display: "flex",
}));

const EditModeText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontWeight: "bold",
}));

const SelectWidthDiv = styled("div")(({ theme }) => ({
    width: "150px",
    marginRight: theme.spacing(2),
    paddingTop: theme.spacing(1.25),
}));

const PaddedButtonBase = styled(ButtonBase)(({ theme }) => ({
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(0.5),
    textTransform: "none",
    marginRight: theme.spacing(2.0),
}));

const iconCommon = {
    fontWeight: "bold",
    fontSize: "1.9em",
    marginRight: 8,
};

function getEditorIconColor(theme, editMode) {
    return editMode
        ? hexToRGB(theme.palette.primary.main, 0.96)
        : theme.palette.primary.main;
}

function getCancelColor(theme) {
    return hexToRGB(theme.palette.red.main, 0.75);
}

function getSaveColor(theme) {
    return hexToRGB(theme.palette.info.dark, 0.96);
}

const saveDefaultIconColor = ThemeColors.purple;

interface OwnProps {
    dashboardStore?: DashboardStore;
    showRefresh?: boolean;
}

type Props = OwnProps;

const DashboardEditor: FunctionComponent<Props> = observer((props) => {
    const theme = useTheme();
    const location = useLocation();
    const editMode = props.dashboardStore?.editMode;
    const authStore = useStore(AuthStore);
    const homeDashboardStore = useStore(AuthStore).homeDashboardStore;
    const signalsPageStore = useStore(SignalsPageStore);
    const dashboardIsSetAsHomeForUser =
        homeDashboardStore.userHomeDashboardId === "reporting";
    const dashboardIsSetAsHomeForOrg =
        homeDashboardStore.organizationHomeDashboardId === "reporting";
    const labelOptions = [
        { label: "25% initial width", value: "1" },
        { label: "50% initial width", value: "2" },
        { label: "75% initial width", value: "3" },
        { label: "100% initial width", value: "4" },
    ];

    return (
        <RootDiv>
            {!location.pathname.includes("agentcoaching") &&
                !dashboardIsSetAsHomeForUser && (
                    <PaddedButtonBase
                        onClick={() => {
                            // Eval reporting isnt a signals dash so we handle the dashId and title separately to track when its set to homepage
                            trackHomePage(
                                "Evaluation Reporting",
                                "Evaluation Reporting",
                                location.pathname,
                            );
                            homeDashboardStore.updateUserHomeDashboardId(
                                "reporting",
                            );
                            if (signalsPageStore.userHomeDashboard) {
                                signalsPageStore.userHomeDashboard.setIsUserHomeDashboard(
                                    false,
                                );
                            }
                        }}
                    >
                        <HomeRounded
                            style={{ color: theme.palette.blue.main }}
                        />
                        <EditModeText>Set As Home</EditModeText>
                    </PaddedButtonBase>
                )}

            {!location.pathname.includes("agentcoaching") &&
                dashboardIsSetAsHomeForUser && (
                    <PaddedButtonBase
                        onClick={() =>
                            homeDashboardStore.removeUserHomeDashboardId()
                        }
                    >
                        <HomeRounded
                            style={{ color: theme.palette.blue.main }}
                        />
                        <EditModeText>Remove As Home</EditModeText>
                    </PaddedButtonBase>
                )}

            {!location.pathname.includes("agentcoaching") &&
                !dashboardIsSetAsHomeForOrg &&
                authStore.canUserEdit("Homepage") && (
                    <PaddedButtonBase
                        onClick={() => {
                            // Eval reporting isnt a signals dash so we handle the dashId and title separately to track when its set to homepage
                            trackHomePage(
                                "Evaluation Reporting",
                                "Evaluation Reporting",
                                location.pathname,
                            );
                            if (signalsPageStore.orgHomeDashboard) {
                                signalsPageStore.orgHomeDashboard.setIsOrgHomeDashboard(
                                    false,
                                );
                            }
                            homeDashboardStore.updateOrgHomeDashboardId(
                                "reporting",
                            );
                        }}
                    >
                        <HomeWorkRounded
                            style={{ color: theme.palette.blue.main }}
                        />
                        <EditModeText>Set As Org Home</EditModeText>
                    </PaddedButtonBase>
                )}

            {!location.pathname.includes("agentcoaching") &&
                dashboardIsSetAsHomeForOrg &&
                authStore.canUserEdit("Homepage") && (
                    <PaddedButtonBase
                        onClick={() =>
                            homeDashboardStore.removeOrgHomeDashboardId()
                        }
                    >
                        <HomeWorkRounded
                            style={{ color: theme.palette.blue.main }}
                        />
                        <EditModeText>Remove As Org Home</EditModeText>
                    </PaddedButtonBase>
                )}

            {props.dashboardStore?.editMode && (
                <SelectWidthDiv>
                    <AcxSelectSingle
                        labelField="label"
                        id="new-widget-width"
                        valueField="value"
                        options={labelOptions}
                        onChange={(item) =>
                            (props.dashboardStore!.initialWidgetWidth =
                                item.value)
                        }
                        defaultValue={labelOptions.find(
                            (opt) =>
                                opt.value ===
                                props.dashboardStore?.initialWidgetWidth,
                        )}
                    />
                </SelectWidthDiv>
            )}
            {props.showRefresh && (
                <PaddedButtonBase onClick={props.dashboardStore?.refreshAll}>
                    <RefreshIcon
                        style={{
                            ...iconCommon,
                            color: getEditorIconColor(theme, editMode),
                        }}
                    />
                    <EditModeText>Refresh</EditModeText>
                </PaddedButtonBase>
            )}

            {!props.dashboardStore?.isAgentAsUser && (
                <>
                    {!props.dashboardStore?.editMode ? (
                        <PaddedButtonBase
                            onClick={props.dashboardStore?.editDashboard}
                        >
                            <EditOutlinedIcon
                                style={{
                                    ...iconCommon,
                                    color: getEditorIconColor(theme, editMode),
                                }}
                            />
                            <EditModeText>Edit</EditModeText>
                        </PaddedButtonBase>
                    ) : (
                        props.dashboardStore.pendingDashboardChanges && (
                            <PaddedButtonBase
                                onClick={props.dashboardStore?.saveChanges}
                            >
                                <SaveAltIcon
                                    style={{
                                        ...iconCommon,
                                        color: getSaveColor(theme),
                                    }}
                                />
                                <EditModeText>Save</EditModeText>
                            </PaddedButtonBase>
                        )
                    )}

                    {props.dashboardStore?.editMode && (
                        <PaddedButtonBase
                            onClick={props.dashboardStore?.cancelEditing}
                        >
                            <CancelPresentationIcon
                                style={{
                                    ...iconCommon,
                                    color: getCancelColor(theme),
                                }}
                            />
                            <EditModeText>Cancel</EditModeText>
                        </PaddedButtonBase>
                    )}

                    {props.dashboardStore?.editMode &&
                        props.dashboardStore.canBuildDefaultDashboard &&
                        props.dashboardStore.pendingDashboardChanges && (
                            <PaddedButtonBase
                                onClick={
                                    props.dashboardStore?.saveChangesAsDefault
                                }
                            >
                                <SaveIcon
                                    style={{
                                        ...iconCommon,
                                        color: saveDefaultIconColor,
                                    }}
                                />
                                <EditModeText>Save Default</EditModeText>
                            </PaddedButtonBase>
                        )}

                    {props.dashboardStore?.editMode &&
                        props.dashboardStore.isCustomUserDashboard && (
                            <PaddedButtonBase
                                onClick={
                                    props.dashboardStore
                                        ?.restoreDefaultDashboard
                                }
                            >
                                <SettingsBackupRestoreIcon
                                    style={{
                                        ...iconCommon,
                                        color: getEditorIconColor(
                                            theme,
                                            editMode,
                                        ),
                                    }}
                                />
                                <EditModeText>Restore Default</EditModeText>
                            </PaddedButtonBase>
                        )}
                </>
            )}
        </RootDiv>
    );
});

export default DashboardEditor;
